/**
 * Shared Give Lively donate drawer (L-018).
 *
 * One source for LMS Mako (classic script) and the MFE chrome App
 * (side-effect import). Markup is injected here so LMS and MFE stay
 * identical. Pills keep href=support as the no-JS fallback.
 *
 * Widget slug/URL is not invented: it is the 2026-09-29 homepage
 * Simple Donation Widget (`brain-and-spine-group`).
 */
(function (global) {
  "use strict";

  const ROOT = "[data-donate-drawer]";
  const OPEN = "a[data-donate-open], button[data-donate-open]";
  const DISMISS = "[data-donate-dismiss]";
  const PANEL = "[data-donate-panel]";
  const WIDGET = "[data-donate-widget]";
  const FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
  const DEFAULTS = {
    widgetSrc: "",
    donateUrl: "https://support.brainandspinegroup.org",
    heading: "100% Community Supported",
    blurb:
      "Community and foundation funding ensures Brain & Spine Group continues to bring open education to the world and mentor students toward becoming the researchers of tomorrow.",
    learnMore: "Learn more",
    closeLabel: "Close donate",
    donateNow: "Donate now",
  };

  const DURATION_MS = 320;

  let bound = false;
  let open = false;
  let lastTrigger = null;
  let config = { ...DEFAULTS };
  let relayoutTimer = 0;

  function esc(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function readConfig() {
    const host = document.getElementById("bsg-donate-config");
    if (!host) return {};
    if (host.tagName === "SCRIPT") {
      try {
        return JSON.parse(host.textContent || "{}") || {};
      } catch {
        return {};
      }
    }
    const data = host.dataset || {};
    return {
      widgetSrc: data.widgetSrc || "",
      donateUrl: data.donateUrl || "",
      heading: data.heading || "",
      blurb: data.blurb || "",
      learnMore: data.learnMore || "",
      closeLabel: data.close || data.closeLabel || "",
      donateNow: data.donateNow || "",
    };
  }

  function renderDrawer(opts) {
    const src = esc(opts.widgetSrc || "");
    return `<div class="bsg-donate" data-donate-drawer hidden>
  <div class="bsg-donate__overlay" data-donate-dismiss tabindex="-1"></div>
  <div class="bsg-donate__panel" data-donate-panel role="dialog" aria-modal="true" aria-labelledby="bsg-donate-title" id="donation-form" tabindex="-1">
    <div class="bsg-donate__head">
      <h2 id="bsg-donate-title" class="bsg-donate__title">${esc(opts.heading || opts.donateNow)}</h2>
      <button type="button" class="bsg-donate__close" data-donate-dismiss aria-label="${esc(opts.closeLabel)}">
        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
    </div>
    <p class="bsg-donate__blurb">${esc(opts.blurb)}</p>
    <div id="give-lively-widget" class="bsg-donate__widget gl-simple-donation-widget give-lively-simple-donation-widget" data-donate-widget data-src="${src}"></div>
    <a class="bsg-donate__more" href="${esc(opts.donateUrl)}">${esc(opts.learnMore)}</a>
  </div>
</div>`;
  }

  function drawerEl() {
    return document.querySelector(ROOT);
  }

  function panelEl() {
    return drawerEl()?.querySelector(PANEL) ?? null;
  }

  function setExpanded(value) {
    document.querySelectorAll(OPEN).forEach((el) => {
      el.setAttribute("aria-expanded", String(value));
    });
  }

  function focusables() {
    const panel = panelEl();
    if (!panel) return [];
    return Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => {
      if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") return false;
      if (el.closest("[hidden]")) return false;
      return el.offsetParent !== null || el === document.activeElement || panel.contains(el);
    });
  }

  function loadWidget() {
    const host = drawerEl()?.querySelector(WIDGET);
    if (!host) return;
    const src = host.getAttribute("data-src") || config.widgetSrc;
    if (!src) return;
    // Give Lively simple_donation waits for #give-lively-widget (and
    // .gl-simple-donation-widget). It writes innerHTML / a shadow host
    // into that node — never nest the script inside it.
    host.id = "give-lively-widget";
    host.classList.add("gl-simple-donation-widget", "give-lively-simple-donation-widget");
    host.setAttribute("data-src", src);
    if (host.dataset.loaded === "true" && (host.querySelector("iframe") || host.querySelector(".gl-widget") || host.querySelector("gl-donation-widget-container"))) {
      return;
    }
    const existing = document.querySelector('script[data-bsg-donate-widget]');
    if (existing) {
      host.dataset.loaded = existing.dataset.error === "true" ? "error" : "true";
      return;
    }
    host.dataset.loaded = "pending";
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.bsgDonateWidget = "true";
    script.addEventListener("error", () => {
      script.dataset.error = "true";
      host.dataset.loaded = "error";
      host.innerHTML = `<p class="bsg-donate__fallback">The donation form could not load. <a href="${esc(config.donateUrl)}">${esc(config.learnMore)}</a></p>`;
    });
    script.addEventListener("load", () => {
      host.dataset.loaded = "true";
    });
    document.body.appendChild(script);
  }

  function prefersReducedMotion() {
    return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches);
  }

  function relayoutWidget() {
    const host = drawerEl()?.querySelector(WIDGET);
    if (!host) return;
    window.dispatchEvent(new Event("resize"));
    host.querySelectorAll("iframe").forEach((frame) => {
      try {
        frame.contentWindow?.dispatchEvent(new Event("resize"));
      } catch {
        /* Give Lively iframe is cross-origin */
      }
    });
  }

  function afterPanelTransition(panel, done) {
    if (!panel || prefersReducedMotion()) {
      done();
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      panel.removeEventListener("transitionend", onEnd);
      done();
    };
    const onEnd = (event) => {
      if (event.target !== panel || event.propertyName !== "transform") return;
      finish();
    };
    panel.addEventListener("transitionend", onEnd);
    window.setTimeout(finish, DURATION_MS + 40);
  }

  function openDrawer(trigger) {
    const root = drawerEl();
    const panel = panelEl();
    if (!root || !panel) return;
    lastTrigger = trigger instanceof Element ? trigger : lastTrigger;
    document.querySelector(".bsg-hdr__disclosure[open]")?.removeAttribute("open");
    root.hidden = false;
    root.removeAttribute("aria-hidden");
    document.documentElement.setAttribute("data-donate-drawer-open", "");
    document.documentElement.style.overflow = "hidden";
    setExpanded(true);
    open = true;
    loadWidget();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!open || drawerEl() !== root) return;
        root.setAttribute("data-open", "");
        afterPanelTransition(panel, relayoutWidget);
      });
    });
    const closeBtn = root.querySelector(".bsg-donate__close");
    (closeBtn || panel).focus();
  }

  function closeDrawer(returnFocus) {
    const root = drawerEl();
    if (!root || !open) return;
    open = false;
    root.removeAttribute("data-open");
    document.documentElement.removeAttribute("data-donate-drawer-open");
    document.documentElement.style.overflow = "";
    setExpanded(false);
    if (location.hash === "#donation-form") {
      const url = new URL(location.href);
      url.hash = "";
      history.replaceState(null, "", url.pathname + url.search);
    }
    const finish = () => {
      if (open) return;
      root.hidden = true;
      root.setAttribute("aria-hidden", "true");
    };
    afterPanelTransition(panelEl(), finish);
    if (returnFocus !== false) lastTrigger?.focus();
  }

  function onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest(OPEN);
    if (trigger) {
      event.preventDefault();
      openDrawer(trigger);
      return;
    }
    if (!open) return;
    if (target.closest(DISMISS) && !target.closest(PANEL)) {
      closeDrawer(true);
      return;
    }
    if (target.closest(".bsg-donate__close")) {
      closeDrawer(true);
    }
  }

  function onKeyDown(event) {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeDrawer(true);
      return;
    }
    if (event.key !== "Tab") return;
    const nodes = focusables();
    if (!nodes.length) {
      event.preventDefault();
      panelEl()?.focus();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !panelEl()?.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !panelEl()?.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function onHash() {
    if (location.hash === "#donation-form") openDrawer(document.querySelector(OPEN));
  }

  function onViewportResize() {
    if (!open) return;
    window.clearTimeout(relayoutTimer);
    relayoutTimer = window.setTimeout(relayoutWidget, 50);
  }

  function ensureDrawer(opts) {
    config = { ...DEFAULTS, ...readConfig(), ...opts };
    Object.keys(config).forEach((key) => {
      if (!config[key]) config[key] = DEFAULTS[key] || config[key];
    });
    const existing = drawerEl();
    if (!existing) {
      document.body.insertAdjacentHTML("beforeend", renderDrawer(config));
    } else if (config.widgetSrc) {
      const host = existing.querySelector(WIDGET);
      if (host) host.setAttribute("data-src", config.widgetSrc);
    }
    document.querySelectorAll(OPEN).forEach((el) => {
      el.setAttribute("aria-haspopup", "dialog");
      el.setAttribute("aria-controls", "donation-form");
      if (!el.hasAttribute("aria-expanded")) el.setAttribute("aria-expanded", "false");
    });
  }

  function bind(opts) {
    ensureDrawer(opts);
    if (bound) {
      if (location.hash === "#donation-form") openDrawer(document.querySelector(OPEN));
      return unbind;
    }
    bound = true;
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("hashchange", onHash);
    window.addEventListener("resize", onViewportResize);
    if (location.hash === "#donation-form") openDrawer(document.querySelector(OPEN));
    return unbind;
  }

  function unbind() {
    if (!bound) return;
    closeDrawer(false);
    document.removeEventListener("click", onClick);
    document.removeEventListener("keydown", onKeyDown, true);
    window.removeEventListener("hashchange", onHash);
    window.removeEventListener("resize", onViewportResize);
    bound = false;
  }

  function auto() {
    if (!document.getElementById("bsg-donate-config") && !document.querySelector(OPEN)) return;
    bind();
  }

  const api = { bind, unbind, open: openDrawer, close: closeDrawer, render: renderDrawer };
  global.bsgDonateDrawer = api;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", auto);
  } else {
    auto();
  }
})(typeof window !== "undefined" ? window : globalThis);